/**
 * PR-E2 — text-run → instanced-glyph-quad geometry builder.
 *
 * Given an `MTextRun` (or a single-line `Text` collapsed to a one-run
 * MText) and a loaded `SdfAtlas`, this module emits the per-instance
 * attribute arrays consumed by the SDF text shader in
 * `textShaders.glsl.ts`:
 *
 *   - `offsets` — Float32Array, 2 floats / instance — glyph quad
 *     lower-left in text-local space.
 *   - `scales` — Float32Array, 2 floats / instance — glyph quad
 *     width × height in the same text-local space.
 *   - `colors` — Float32Array, 3 floats / instance — RGB color
 *     (parsed from the run's `colorHex`).
 *   - `uvMin` / `uvMax` — Float32Array, 2 floats / instance —
 *     atlas-space UVs of the glyph's bottom-left and top-right
 *     corners. Stored separately so the vertex shader can mix them
 *     against the unit-quad position without a 4-vertex-per-instance
 *     UV buffer.
 *   - `heights` — Float32Array, 1 float / instance — glyph height in
 *     world units (= run height × glyph metric ratio). The shader
 *     uses this to compute screenPxRange.
 *
 * The `vertices` and `uvs` outputs are kept on the API for symmetry
 * with the planned non-instanced fallback (some WebGL1 drivers do not
 * support instanced attributes without `ANGLE_instanced_arrays` — the
 * fallback packs four full vertices per glyph). PR-E2 ships only the
 * instanced path; the non-instanced arrays are emitted empty in this
 * iteration. (Plan §5 PR-E5 lands the non-instanced fallback.)
 *
 * # Cursor advance + bearing
 *
 * Atlas metrics come from BMFont in font-design pixel units (`u, v, w,
 * h, advance, bearingX, bearingY`). The renderer scales every metric
 * by `heightScale = run.heightWorld / atlas.lineHeight` so the
 * resulting text-local space units match the run's world height.
 *
 *   - Glyph quad lower-left X = `cursor.x + bearingX * heightScale`.
 *   - Glyph quad lower-left Y = `cursor.y + (bearingY - h) *
 *     heightScale` — bearingY is the distance from the baseline to
 *     the top of the glyph; subtracting `h` gives the bottom.
 *   - Quad width = `w * heightScale`.
 *   - Quad height = `h * heightScale`.
 *   - Cursor advance = `advance * heightScale * widthFactor`.
 *
 * The Y axis follows AutoCAD-world convention (Y-up). The atlas PNG
 * is committed with top-left origin (`flipY: false` on the THREE
 * texture) but the UV mapping below converts to bottom-left so the
 * shader receives a consistent atlas-Y-up coordinate space.
 *
 * # Q15.C fallback path
 *
 * If `atlas.glyphs.get(charCode) === undefined`, the renderer first
 * tries the `?` glyph (codepoint 63) so missing characters get a
 * visible substitute. If the atlas itself doesn't contain `?` the
 * glyph is rendered as an empty rectangle of average size — the
 * renderer never silently drops a character.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1 — full fidelity
 *
 * Every glyph metric is preserved (bearing, advance, kerning is not
 * yet used at PR-E2 — kerning lands with PR-E3's `\T` tracking). No
 * skipping of "unsupported" code points — fallback always fires.
 */

import type { RenderableEntity, Vec3 } from "../types";

import type { GlyphMetric, SdfAtlas } from "./sdfAtlasLoader";
import { MissingAtlasError } from "./sdfAtlasLoader";
import { shouldSkipTextRun } from "./textScreenGate";

/**
 * PR-F — Q29.A inline embedded-block resolver hook.
 *
 * Called by `renderTextRun` when a run carries `embeddedBlockName`.
 * The resolver flattens the named block at the run height, anchored
 * so the block's bbox lower-left lands at `cursorPos`, and returns:
 *
 *   - `primitives`: flattened `RenderableEntity[]` ready to feed
 *     through the regular line / mesh pipelines (NOT via glyph atlas).
 *   - `bboxWidth`: world-space inline width — drives cursor advance.
 *   - `bboxHeight`: world-space inline height (parity sanity check).
 *
 * Returning `null` signals "block missing or empty" — the caller is
 * expected to substitute the `[BLOCK]` placeholder text run via
 * `buildEmbeddedBlockPlaceholder` BEFORE invoking `renderTextRun`,
 * so a null here just means the run emits nothing and the cursor
 * does not advance (the placeholder path renders separately).
 *
 * The hook is dependency-injected (rather than imported directly)
 * so `textRunRenderer.ts` stays decoupled from `blockInsert.ts` and
 * `mtextEmbeddedBlock.ts` — the wiring lives at the Scene3DStage
 * call site where the document `blocks` map and `AdapterContext` are
 * already in scope.
 */
export type EmbeddedBlockResolver = (args: {
  blockName: string;
  cursorPos: Vec3;
  runHeight: number;
}) => { primitives: RenderableEntity[]; bboxWidth: number; bboxHeight: number } | null;

/**
 * One resolved inline embedded block, accumulated on
 * `TextRunGeometry.embeddedBlocks` so the caller can mount the
 * primitives into the scene alongside the glyph mesh.
 */
export interface ResolvedEmbeddedBlock {
  /** Block name from the `\f<Name>;` directive. */
  blockName: string;
  /** World-space cursor position where the block bbox lower-left was anchored. */
  cursorPos: Vec3;
  /** Inline width — already factored into `cursorEndX`. */
  bboxWidth: number;
  /** Inline height — equals the run height post-scale. */
  bboxHeight: number;
  /** Flattened primitives ready to mount into the scene graph. */
  primitives: RenderableEntity[];
}

/** Subset of MTextRun fields the renderer cares about. */
export interface TextRunInput {
  text: string;
  /** Run height in world units (matches `MTextRun.heightWorld`). */
  heightWorld: number;
  /** AutoCAD width factor (`\W`). 1 = identity scale. */
  widthFactor: number;
  /** RGB color, e.g. `#ff0000`. */
  colorHex: string;
  /**
   * PR-F — Q29.A inline embedded-block reference. When set, the run
   * emits no glyph instances; instead `resolveEmbeddedBlock` is
   * invoked at the current cursor position, the primitives are
   * accumulated into `TextRunGeometry.embeddedBlocks`, and the cursor
   * advances by the resolver's reported `bboxWidth × widthFactor`.
   *
   * If `resolveEmbeddedBlock` is unset OR returns `null`, the run is
   * a no-op (zero glyphs, zero advance) — the caller is expected to
   * have already substituted the `[BLOCK]` placeholder text run via
   * `buildEmbeddedBlockPlaceholder` for the unresolved case.
   */
  embeddedBlockName?: string;
  /**
   * PR-F — dependency-injected block flatten hook. See
   * `EmbeddedBlockResolver` JSDoc above for full semantics.
   */
  resolveEmbeddedBlock?: EmbeddedBlockResolver;
  /**
   * PR-E3 — letter tracking factor (`\T`). Multiplies the cursor
   * advance per glyph. Default 1.
   */
  trackingFactor?: number;
  /**
   * PR-E3 — oblique angle in radians (`\Q` is degrees, the parser
   * converts on read). Stamped onto every glyph instance so the
   * vertex shader can shear the quad. Default 0.
   */
  oblique?: number;
  /**
   * PR-E3 — when true, emit an underline decoration quad after the
   * run's glyph quads. Decoration height = max(heightWorld * 0.05, 1
   * world unit) so the line stays legible at any zoom.
   */
  underline?: boolean;
  /** PR-E3 — same as `underline` but above the glyph. */
  overline?: boolean;
  /** PR-E3 — same as `underline` but at glyph mid-height. */
  strikethrough?: boolean;
  /**
   * PR-E3 — alignment-driven baseline shift (`\A`). Added to the
   * cursor.y before each glyph; zero leaves the run on the baseline.
   */
  baselineOffsetWorld?: number;
  /** Optional starting cursor X in text-local space. Default 0. */
  cursorStartX?: number;
  /** Optional starting cursor Y in text-local space. Default 0. */
  cursorStartY?: number;
  /**
   * PR-V5 — optional camera zoom (CSS px / world unit). When provided
   * AND `runHeightWorld × cameraZoom < 0.5`, the run is dropped sub-pixel
   * and the renderer returns empty geometry (no glyph quads, no
   * decorations, no embedded blocks). When `<2` and `≥0.5` the run is
   * still emitted at full fidelity from this function — the caller
   * (Scene3DStage) collapses tiny entities to a single Points dot at
   * the *entity* level via `shouldSkipTextRun(...) === "dot"`. Omitting
   * the field disables the gate entirely (= full-fidelity path).
   *
   * The gate is an emit-time optimization (CLAUDE.md PRIORITET #1):
   * the moment the camera zooms back in past the cutoff the full
   * renderer path runs unmodified.
   */
  cameraZoom?: number | undefined;
}

/** Output buffers consumed by the SDF text material. */
export interface TextRunGeometry {
  /** Reserved for the non-instanced fallback (PR-E5). Empty in PR-E2. */
  vertices: Float32Array;
  /** Reserved for the non-instanced fallback (PR-E5). Empty in PR-E2. */
  uvs: Float32Array;
  /** Per-instance RGB color, 3 floats / instance. */
  colors: Float32Array;
  /** Per-instance quad lower-left, 2 floats / instance. */
  offsets: Float32Array;
  /** Per-instance quad width × height, 2 floats / instance. */
  scales: Float32Array;
  /** Per-instance quad height (world units), 1 float / instance. */
  heights: Float32Array;
  /** Per-instance atlas UV lower-left, 2 floats / instance. */
  uvMin: Float32Array;
  /** Per-instance atlas UV upper-right, 2 floats / instance. */
  uvMax: Float32Array;
  /**
   * PR-E3 — per-instance oblique angle in radians, 1 float /
   * instance. Forwarded to the vertex shader's `instance_oblique`.
   */
  obliques: Float32Array;
  /**
   * PR-E3 — per-instance decoration flag, 1 float / instance.
   *   0 = glyph quad (sample atlas).
   *   1 = solid decoration line (underline / overline / strike).
   * Forwarded to the vertex shader's `instance_decoration`.
   */
  decorations: Float32Array;
  /** Number of glyph instances emitted. */
  instanceCount: number;
  /** End cursor X (lets the caller chain runs without re-walking). */
  cursorEndX: number;
  /** Whether any glyph hit the Q15.C `?` fallback path. */
  fallbackUsed: boolean;
  /**
   * PR-F — Q29.A inline embedded blocks resolved during this run.
   * Always present (empty array when no embedded blocks emitted) so
   * call sites can iterate without null-checking.
   */
  embeddedBlocks: ResolvedEmbeddedBlock[];
}

const EMPTY_GEOMETRY: TextRunGeometry = {
  vertices: new Float32Array(0),
  uvs: new Float32Array(0),
  colors: new Float32Array(0),
  offsets: new Float32Array(0),
  scales: new Float32Array(0),
  heights: new Float32Array(0),
  uvMin: new Float32Array(0),
  uvMax: new Float32Array(0),
  obliques: new Float32Array(0),
  decorations: new Float32Array(0),
  instanceCount: 0,
  cursorEndX: 0,
  fallbackUsed: false,
  embeddedBlocks: [],
};

/**
 * Convert a `#rrggbb` (or `#rgb`, or bare `rrggbb`) color into a
 * normalized RGB triple. Returns `[1, 1, 1]` (white) for unparseable
 * input — the caller's color resolution should already have produced
 * a valid hex, so this is a safety net.
 */
export function parseColorHexRgb(colorHex: string): [number, number, number] {
  let raw = colorHex.trim();
  if (raw.startsWith("#")) raw = raw.slice(1);
  if (raw.length === 3) {
    // expand short form `#abc` → `aabbcc`
    raw = raw
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (raw.length !== 6) return [1, 1, 1];
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) {
    return [1, 1, 1];
  }
  return [r / 255, g / 255, b / 255];
}

/**
 * Build glyph quad attribute arrays for one text run.
 *
 * Returns `EMPTY_GEOMETRY` for empty input or for runs whose every
 * glyph misses both the primary lookup and the `?` fallback. The
 * caller (Scene3DStage) sees `instanceCount === 0` and skips mesh
 * creation.
 *
 * `MissingAtlasError` is propagated unchanged — the caller handles
 * the placeholder rectangle path at scene-mount time, before this
 * function is invoked.
 */
export function renderTextRun(
  run: TextRunInput,
  atlas: SdfAtlas,
): TextRunGeometry {
  // PR-F — Q29.A inline embedded-block branch. Runs flagged with
  // `embeddedBlockName` skip the glyph atlas entirely: the resolver
  // flattens the named block at the run height, anchors its bbox
  // lower-left at (cursorStartX, cursorStartY + baselineOffset), and
  // the cursor advances by `bboxWidth × widthFactor`. The caller is
  // expected to have substituted a `[BLOCK]` placeholder text run for
  // the unresolved case BEFORE invoking `renderTextRun`, so a missing
  // resolver / null result here is a true no-op (zero glyphs, zero
  // advance, empty embedded-blocks array). Decorations (`\L \O \K`)
  // are not emitted around an embedded-block run because AutoCAD does
  // not draw decoration lines through inline block content.
  if (run.embeddedBlockName !== undefined && run.embeddedBlockName !== "") {
    const widthFactorEb = run.widthFactor > 0 ? run.widthFactor : 1;
    const baselineOffsetEb = Number.isFinite(run.baselineOffsetWorld)
      ? (run.baselineOffsetWorld as number)
      : 0;
    const cursorStartXEb = run.cursorStartX ?? 0;
    const cursorYEb = (run.cursorStartY ?? 0) + baselineOffsetEb;

    const resolver = run.resolveEmbeddedBlock;
    if (!resolver) {
      // No resolver → no-op (placeholder path is the caller's job).
      return {
        ...EMPTY_GEOMETRY,
        cursorEndX: cursorStartXEb,
        embeddedBlocks: [],
      };
    }
    const cursorPos: Vec3 = { x: cursorStartXEb, y: cursorYEb, z: 0 };
    const resolved = resolver({
      blockName: run.embeddedBlockName,
      cursorPos,
      runHeight: run.heightWorld,
    });
    if (!resolved || resolved.primitives.length === 0) {
      // Block missing or empty — no advance, no primitives.
      return {
        ...EMPTY_GEOMETRY,
        cursorEndX: cursorStartXEb,
        embeddedBlocks: [],
      };
    }
    // Cursor advance = bboxWidth × widthFactor (matches AutoCAD's
    // inline block advance reference). Tracking factor is NOT applied
    // here — `\T` tracking governs glyph-to-glyph spacing, not block
    // post-trailing space. The next run starts immediately after.
    const cursorEndX = cursorStartXEb + resolved.bboxWidth * widthFactorEb;
    return {
      ...EMPTY_GEOMETRY,
      cursorEndX,
      embeddedBlocks: [
        {
          blockName: run.embeddedBlockName,
          cursorPos,
          bboxWidth: resolved.bboxWidth,
          bboxHeight: resolved.bboxHeight,
          primitives: resolved.primitives,
        },
      ],
    };
  }

  if (!run.text || run.text.length === 0) return EMPTY_GEOMETRY;

  // PR-V5 — sub-pixel screen-size gate. When a camera zoom is supplied
  // and the run height is below the 0.5 px floor, skip every glyph +
  // decoration emission entirely. The "dot" representation is owned by
  // the Scene3DStage entity-mesh builder, not the per-run renderer, so
  // a "dot"-classified run still falls through to the full path here
  // (the entity-level gate caught it earlier if the whole entity was
  // dot-collapsible). See `textScreenGate.ts` for thresholds.
  if (run.cameraZoom !== undefined) {
    const bucket = shouldSkipTextRun(run.heightWorld, run.cameraZoom);
    if (bucket === "skip") {
      return {
        ...EMPTY_GEOMETRY,
        cursorEndX: run.cursorStartX ?? 0,
        embeddedBlocks: [],
      };
    }
  }

  const heightScale = run.heightWorld / atlas.lineHeight;
  if (!Number.isFinite(heightScale) || heightScale <= 0) return EMPTY_GEOMETRY;

  const [r, g, b] = parseColorHexRgb(run.colorHex);
  const widthFactor = run.widthFactor > 0 ? run.widthFactor : 1;
  const trackingFactor =
    run.trackingFactor !== undefined && run.trackingFactor > 0
      ? run.trackingFactor
      : 1;
  const oblique = Number.isFinite(run.oblique) ? (run.oblique as number) : 0;
  const baselineOffset = Number.isFinite(run.baselineOffsetWorld)
    ? (run.baselineOffsetWorld as number)
    : 0;
  const wantUnderline = run.underline === true;
  const wantOverline = run.overline === true;
  const wantStrike = run.strikethrough === true;

  // Worst-case capacity: one glyph instance per code unit + up to
  // three decoration quads (underline, overline, strikethrough). The
  // trailing slack is sliced away before the return.
  const decorationSlots =
    (wantUnderline ? 1 : 0) + (wantOverline ? 1 : 0) + (wantStrike ? 1 : 0);
  const maxInstances = run.text.length + decorationSlots;
  const offsets = new Float32Array(maxInstances * 2);
  const scales = new Float32Array(maxInstances * 2);
  const colors = new Float32Array(maxInstances * 3);
  const heights = new Float32Array(maxInstances);
  const uvMin = new Float32Array(maxInstances * 2);
  const uvMax = new Float32Array(maxInstances * 2);
  const obliques = new Float32Array(maxInstances);
  const decorations = new Float32Array(maxInstances);

  const fallbackGlyph = atlas.glyphs.get(0x3f); // '?'
  const cursorStartX = run.cursorStartX ?? 0;
  let cursorX = cursorStartX;
  const cursorY = (run.cursorStartY ?? 0) + baselineOffset;

  let i = 0; // instance write index
  let fallbackUsed = false;
  // `for...of` over a string iterates by code points (handles surrogate
  // pairs correctly), so emoji / supplementary-plane chars look up via
  // the right codepoint.
  for (const ch of run.text) {
    const code = ch.codePointAt(0);
    if (code === undefined) continue;

    let glyph: GlyphMetric | undefined = atlas.glyphs.get(code);
    if (!glyph) {
      glyph = fallbackGlyph;
      if (glyph) fallbackUsed = true;
    }

    if (!glyph) {
      // Both the source codepoint AND the `?` fallback are missing.
      // Emit a placeholder advance based on lineHeight so the run
      // doesn't collapse to zero width.
      cursorX +=
        atlas.lineHeight * heightScale * 0.5 * widthFactor * trackingFactor;
      continue;
    }

    // Whitespace glyphs (w === 0 or h === 0) advance the cursor but
    // don't emit a quad — atlas dimension would create a degenerate
    // sampling rectangle.
    if (glyph.w > 0 && glyph.h > 0) {
      const quadWidth = glyph.w * heightScale;
      const quadHeight = glyph.h * heightScale;
      const quadX = cursorX + glyph.bearingX * heightScale;
      const quadY = cursorY + (glyph.bearingY - glyph.h) * heightScale;

      offsets[i * 2] = quadX;
      offsets[i * 2 + 1] = quadY;
      scales[i * 2] = quadWidth;
      scales[i * 2 + 1] = quadHeight;
      colors[i * 3] = r;
      colors[i * 3 + 1] = g;
      colors[i * 3 + 2] = b;
      heights[i] = quadHeight;
      obliques[i] = oblique;
      decorations[i] = 0; // glyph quad, sample atlas

      // BMFont atlas: top-left origin in PNG pixels (`flipY: false`).
      // Convert to UV with v_top = u_v / atlasSize and v_bottom =
      // (u_v + h) / atlasSize. We swap min/max on the V axis so the
      // shader's `mix(uv_min, uv_max, unitCorner)` picks v_top when
      // unitCorner.y == 1.0 (top of the unit quad maps to top of the
      // glyph in the PNG, since the quad's Y-up convention matches
      // baseline-up which matches the glyph's bearingY direction).
      const uMin = glyph.u / atlas.atlasSize;
      const uMax = (glyph.u + glyph.w) / atlas.atlasSize;
      const vTop = glyph.v / atlas.atlasSize;
      const vBottom = (glyph.v + glyph.h) / atlas.atlasSize;
      uvMin[i * 2] = uMin;
      uvMin[i * 2 + 1] = vBottom;
      uvMax[i * 2] = uMax;
      uvMax[i * 2 + 1] = vTop;

      i += 1;
    }

    cursorX += glyph.advance * heightScale * widthFactor * trackingFactor;
  }

  // PR-E3 — emit decoration quads after every glyph so they share the
  // run's color / oblique. Each decoration spans the full glyph run
  // (cursorStartX → cursorX) at a fixed Y offset relative to baseline.
  // Decoration thickness defaults to 5% of the glyph height with a
  // floor of 1 world unit so it stays visible at any zoom.
  //
  // Skip decorations when no glyphs were emitted — otherwise a run
  // whose every codepoint missed both the primary and `?` fallback
  // would still draw a decoration line over empty space.
  const runWidthWorld = cursorX - cursorStartX;
  if (i > 0 && runWidthWorld > 0 && (wantUnderline || wantOverline || wantStrike)) {
    const decorationThickness = Math.max(run.heightWorld * 0.05, 1);

    const emitDecoration = (yOffset: number): void => {
      offsets[i * 2] = cursorStartX;
      offsets[i * 2 + 1] = cursorY + yOffset;
      scales[i * 2] = runWidthWorld;
      scales[i * 2 + 1] = decorationThickness;
      colors[i * 3] = r;
      colors[i * 3 + 1] = g;
      colors[i * 3 + 2] = b;
      heights[i] = decorationThickness;
      obliques[i] = oblique;
      decorations[i] = 1;
      // UV irrelevant — fragment shader skips the atlas lookup when
      // `v_decoration > 0.5`. Zero them for determinism.
      uvMin[i * 2] = 0;
      uvMin[i * 2 + 1] = 0;
      uvMax[i * 2] = 0;
      uvMax[i * 2 + 1] = 0;
      i += 1;
    };

    if (wantUnderline) {
      // Sit decoration thickness BELOW the baseline so descenders
      // (g, p, y) read clearly.
      emitDecoration(-decorationThickness * 1.5);
    }
    if (wantOverline) {
      // Just above the cap height (≈ run height × 0.8).
      emitDecoration(run.heightWorld * 0.8);
    }
    if (wantStrike) {
      // Through the run's mid-height (x-height ≈ 0.4 of run height).
      emitDecoration(run.heightWorld * 0.4);
    }
  }

  if (i === 0) {
    return {
      ...EMPTY_GEOMETRY,
      cursorEndX: cursorX,
      fallbackUsed,
      embeddedBlocks: [],
    };
  }

  // Slice down to the actual instance count — surrogate pairs and
  // skipped whitespace glyphs leave trailing zeros otherwise.
  return {
    vertices: new Float32Array(0),
    uvs: new Float32Array(0),
    offsets: offsets.slice(0, i * 2),
    scales: scales.slice(0, i * 2),
    colors: colors.slice(0, i * 3),
    heights: heights.slice(0, i),
    uvMin: uvMin.slice(0, i * 2),
    uvMax: uvMax.slice(0, i * 2),
    obliques: obliques.slice(0, i),
    decorations: decorations.slice(0, i),
    instanceCount: i,
    cursorEndX: cursorX,
    fallbackUsed,
    embeddedBlocks: [],
  };
}

export { MissingAtlasError };
